/**
 * Peerless Lawn Care — Responsive Parallax & Depth Engine
 * Caches coordinates, checks boundaries, and handles sticky emulation.
 */

export function initParallax() {
  const parallaxTexts = document.querySelectorAll('[data-parallax]');
  const parallaxImages = document.querySelectorAll('[data-parallax-img]');
  const stickyElements = document.querySelectorAll('.work-sticky, .services-sticky-left, .cap-sticky, .why-us-sticky-left');
  
  let imageCache = [];
  let textCache = [];
  let stickyCaches = [];
  let viewportHeight = window.innerHeight;

  // 4. About Section — Depth Column Parallax
  // Gallery (left) and Map (right) scroll at different rates for a split-depth effect
  const aboutSection = document.querySelector('.work-section');
  const galleryCol = document.querySelector('.work-left-col');
  const mapCol = document.querySelector('.work-content.map-system-container');
  let aboutCache = null;
  // Interpolated current values for ultra-smooth rendering
  let galleryCurrent = 0;
  let mapCurrent = 0;

  // Cache layout boundaries to prevent layout thrashing inside event loops
  const updateMetricsCache = () => {
    viewportHeight = window.innerHeight;
    
    // Retrieve absolute translation scroll coordinates to prevent layout offset caching bugs
    const activeScrollY = (document.body.classList.contains('has-smooth-scroll') && typeof window.scrollCurrentY !== 'undefined')
      ? window.scrollCurrentY
      : window.scrollY;
    
    // Cache floating text boundaries
    textCache = Array.from(parallaxTexts).map(text => {
      const container = text.closest('section') || text.parentElement;
      const rect = container.getBoundingClientRect();
      const absoluteTop = rect.top + activeScrollY;
      
      return {
        element: text,
        containerTop: absoluteTop,
        containerHeight: rect.height,
        speed: parseFloat(text.getAttribute('data-parallax')) || -0.2
      };
    });

    // Cache card image boundaries
    imageCache = Array.from(parallaxImages).map(img => {
      const container = img.closest('.parallax-container') || img.parentElement;
      const rect = container.getBoundingClientRect();
      const absoluteTop = rect.top + activeScrollY;
      
      return {
        element: img,
        containerTop: absoluteTop,
        containerHeight: rect.height,
        speed: parseFloat(img.getAttribute('data-parallax-img')) || 0.1
      };
    });

    // Cache sticky elements dimensions
    stickyCaches = Array.from(stickyElements).map(el => {
      const container = el.parentElement;
      if (!container) return null;
      const rect = container.getBoundingClientRect();
      const absoluteTop = rect.top + activeScrollY;
      
      return {
        element: el,
        containerTop: absoluteTop,
        containerHeight: rect.height,
        stickyHeight: el.getBoundingClientRect().height
      };
    }).filter(Boolean);

    // Cache about section depth-column layout
    if (aboutSection && galleryCol && mapCol) {
      const sectionRect = aboutSection.getBoundingClientRect();
      aboutCache = {
        sectionTop: sectionRect.top + activeScrollY,
        sectionHeight: sectionRect.height
      };
    }
  };

  updateMetricsCache();
  window.addEventListener('resize', updateMetricsCache);
  window.addEventListener('content-update', () => {
    updateMetricsCache();
    setTimeout(updateMetricsCache, 100);
    setTimeout(updateMetricsCache, 250);
    setTimeout(updateMetricsCache, 500);
  });
  
  const performParallaxScroll = (scrollY, velocity) => {
    // Responsive guard: Cancel calculations on mobile viewports
    const isMobile = window.innerWidth <= 900 || ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    if (isMobile) {
      textCache.forEach(item => item.element.style.transform = '');
      imageCache.forEach(item => item.element.style.transform = '');
      stickyCaches.forEach(item => item.element.style.transform = '');
      if (galleryCol) galleryCol.style.transform = '';
      if (mapCol) mapCol.style.transform = '';
      galleryCurrent = 0;
      mapCurrent = 0;
      return;
    }

    // 1. Floating typography parallax with viewport intersection checks
    textCache.forEach(item => {
      const { element, containerTop, containerHeight, speed } = item;
      const elementViewTop = containerTop - scrollY;
      const elementViewBottom = elementViewTop + containerHeight;

      if (elementViewBottom > 0 && elementViewTop < viewportHeight) {
        const translation = scrollY * speed;
        element.style.transform = `translate3d(0, ${translation}px, 0)`;
      }
    });

    // 2. Card image window parallax sliding & velocity micro-scaling
    imageCache.forEach(item => {
      const { element, containerTop, containerHeight, speed } = item;
      const elementViewTop = containerTop - scrollY;
      const elementViewBottom = elementViewTop + containerHeight;

      if (elementViewBottom > 0 && elementViewTop < viewportHeight) {
        const totalViewportRange = viewportHeight + containerHeight;
        const progress = (viewportHeight - elementViewTop) / totalViewportRange;
        const maxOffsetRange = containerHeight * speed * 2;
        const scrollOffset = (progress - 0.5) * maxOffsetRange;
        
        // Elastic scale responsive to scroll velocity
        const dynamicScale = 1.0 + Math.min(Math.abs(velocity) * 0.00025, 0.02);
        
        element.style.transform = `translate3d(0, ${scrollOffset}px, 0) scale(${dynamicScale})`;
      }
    });

    // 3. Sticky left column emulation under transformed scroll-container (centers vertically in viewport)
    stickyCaches.forEach(cache => {
      const { element, containerTop, containerHeight, stickyHeight } = cache;
      
      // Center the sticky element vertically if viewport is taller than element, otherwise apply top padding
      const minPadding = 32; // 2rem safety padding
      const viewportCenterOffset = stickyHeight + minPadding * 2 < viewportHeight 
        ? (viewportHeight - stickyHeight) / 2 
        : minPadding;
      
      // Maximum translation that keeps the element's bottom edge above the viewport's bottom edge
      // This prevents the sticky title from scrolling below the visible window in shorter sections
      const bottomPadding = 48; // ~3rem gap from the viewport bottom (matches visual gap in taller sections)
      const viewportMaxTranslation = scrollY + viewportHeight - containerTop - stickyHeight - bottomPadding;
      const containerMaxTranslation = containerHeight - stickyHeight;
      
      let stickyTranslation = 0;
      
      if (scrollY + viewportCenterOffset < containerTop) {
        // Scroll hasn't reached the sticking start threshold (element at the top of container)
        stickyTranslation = 0;
      } else if (scrollY + viewportCenterOffset + stickyHeight <= containerTop + containerHeight) {
        // Sticking active: Translate element down to center it vertically in the viewport
        stickyTranslation = scrollY + viewportCenterOffset - containerTop;
      } else {
        // Reached section bottom bounds: Stop sticking and align with the bottom of the container
        stickyTranslation = containerHeight - stickyHeight;
      }
      
      // Clamp: never exceed the container's own height, AND never push below the viewport bottom
      stickyTranslation = Math.max(0, Math.min(stickyTranslation, containerMaxTranslation, viewportMaxTranslation));
      element.style.transform = `translate3d(0, ${stickyTranslation}px, 0)`;
    });

    // 4. About Section — Differential depth-column parallax
    if (aboutCache && galleryCol && mapCol) {
      const { sectionTop, sectionHeight } = aboutCache;
      const sectionViewTop = sectionTop - scrollY;
      const sectionViewBottom = sectionViewTop + sectionHeight;

      // Only compute when the section is near or inside the viewport
      if (sectionViewBottom > -200 && sectionViewTop < viewportHeight + 200) {
        // Normalised scroll progress through the section: 0 = section entering, 1 = section leaving
        const totalRange = viewportHeight + sectionHeight;
        const progress = (viewportHeight - sectionViewTop) / totalRange; // 0 → 1
        const centeredProgress = progress - 0.5; // -0.5 → +0.5

        // Target translations — gallery lags (negative speed), map leads (positive speed)
        const gallerySpeed = -0.06;
        const mapSpeed = 0.045;
        const maxTravel = sectionHeight; // Scale travel relative to section height

        const galleryTarget = centeredProgress * maxTravel * gallerySpeed;
        const mapTarget = centeredProgress * maxTravel * mapSpeed;

        // Smooth interpolation (lerp) for ultra-fluid motion — higher factor = more responsive
        const lerpFactor = 0.08;
        galleryCurrent += (galleryTarget - galleryCurrent) * lerpFactor;
        mapCurrent += (mapTarget - mapCurrent) * lerpFactor;

        galleryCol.style.transform = `translate3d(0, ${galleryCurrent}px, 0)`;
        mapCol.style.transform = `translate3d(0, ${mapCurrent}px, 0)`;
      }
    }
  };

  // Listen to the custom smooth scroll event
  window.addEventListener('smooth-scroll', (e) => {
    performParallaxScroll(e.detail.y, e.detail.velocity);
  });

  // Listen to the native scroll event (active only when smooth-scroll is unmounted)
  let lastScrollY = window.scrollY;
  window.addEventListener('scroll', () => {
    // If smooth scroll is active, ignore native scroll (smooth-scroll event drives the updates)
    if (document.body.classList.contains('has-smooth-scroll')) {
      return;
    }
    const currentScrollY = window.scrollY;
    const velocity = currentScrollY - lastScrollY;
    lastScrollY = currentScrollY;
    performParallaxScroll(currentScrollY, velocity);
  }, { passive: true });

  // Recalculate metrics on load
  window.addEventListener('load', updateMetricsCache);

  // Recalculate when fonts are ready (prevents incorrect layout height caching due to font-swapping shifts)
  if (document.fonts) {
    document.fonts.ready.then(updateMetricsCache);
  }

  // Fallback delayed recalibrations to ensure final layout measurements are captured
  setTimeout(updateMetricsCache, 500);
  setTimeout(updateMetricsCache, 1500);
}
